import { useState, useEffect, useRef, lazy, Suspense } from 'react'
import { supabase } from '../lib/supabase'
import {
  loadGroupMessagesWithReactions,
  sendGroupMessage,
  softDeleteMessage,
  subscribeToGroupMessages,
  addReaction,
  removeReaction,
  subscribeToGroupReactions,
  fileContentReport,
  blockUser,
} from '../lib/db'
import { containsProfanity } from '../lib/profanityFilter'
import { timeAgo } from '../lib/time'
import { log } from '../lib/log'

// A2 — emoji picker is lazy-loaded so the initial chat bundle pays 0 KB
// for it. First picker-open downloads ~28 KB component chunk; emoji data
// (~250 KB JSON) fetched from JSDelivr on demand via the `data` prop's
// async loader (see render below). Both cached after first open.
const FullEmojiPicker = lazy(() => import('@emoji-mart/react'))
const EMOJI_DATA_URL  = 'https://cdn.jsdelivr.net/npm/@emoji-mart/data'

// Quick-row emojis (8 fixed). ❤️ MUST be the 2-codepoint sequence
// U+2764 + U+FE0F (VS-16) for emoji-style rendering on platforms that
// otherwise show the bare U+2764 as a black text glyph.
const QUICK_REACTIONS = ['👍', '❤️', '😂', '🔥', '💯', '😭', '🤡', '🎯']

// Animation window for the scale-pop on reaction add/count-change. CSS
// transition runs scale 1 → 1.2 → 1 over this duration.
const REACTION_POP_MS = 200

// A3 — render-side parser for @mention chips. Returns an array of React
// nodes (strings + <span class="mention-chip"> elements) suitable for
// rendering inline inside the message bubble.
//
// Plain-text storage: the literal "@username" string lives in
// group_messages.body verbatim. This parser converts those literals to
// chips at render time. Historical accuracy: if a user changes their
// username later, old messages keep showing the old @text — by design,
// no retroactive lookup. Don't "improve" this into a join against current
// profile rows.
//
// Regex uses a lookbehind to skip email-in-text like "user@example.com" —
// the '@' must NOT be preceded by [A-Za-z0-9_.]. Username regex matches
// typical names (letters/digits/underscore/period); spaces, Unicode, and
// other punctuation are an acknowledged v1 limitation (documented in
// .claude/chat-a3-snapshot.md §5).
//
// Match is case-insensitive: typed "@JoeBob" matches a member with
// username "joebob"; the rendered chip preserves the user's typed casing.
const MENTION_REGEX = /(?<![A-Za-z0-9_.])@[A-Za-z0-9_.]+/g

function renderBodyWithMentions(body, validUsernamesSet, currentUserUsername) {
  if (!body) return body
  const result = []
  let lastIdx = 0
  let match
  // Re-create regex per call so lastIndex state is fresh (the /g flag
  // makes the regex object stateful; reusing it across calls would
  // skip matches non-deterministically).
  const regex = new RegExp(MENTION_REGEX.source, 'g')
  while ((match = regex.exec(body)) !== null) {
    if (match.index > lastIdx) result.push(body.slice(lastIdx, match.index))
    const typedText    = match[0]                  // e.g. "@JoeBob"
    const usernameOnly = typedText.slice(1)        // strip leading @
    const usernameLower = usernameOnly.toLowerCase()
    if (validUsernamesSet.has(usernameLower)) {
      const isSelf = currentUserUsername
        && usernameLower === currentUserUsername.toLowerCase()
      result.push(
        <span
          key={`m-${match.index}`}
          className={`mention-chip${isSelf ? ' is-self' : ''}`}
        >
          {typedText}
        </span>
      )
    } else {
      // No member match — render as plain text, no chip.
      result.push(typedText)
    }
    lastIdx = match.index + typedText.length
  }
  if (lastIdx < body.length) result.push(body.slice(lastIdx))
  return result.length > 0 ? result : body
}

// A1 — base group chat. Realtime postgres-changes subscription on
// group_messages filtered by group_id. Optimistic UI on send; soft-delete
// authority per RLS (own messages, or any message if group creator).
//
// Initial load = last 50 newest-first → reversed for display (oldest at
// top, newest at bottom). No pagination in this build.
//
// Auto-scroll: snaps to bottom on new message arrival ONLY when the user
// was already near the bottom (don't yank someone scrolled up to read
// history). Threshold = 80px from bottom.

const SCROLL_BOTTOM_THRESHOLD = 80
const MAX_LEN = 2000

export default function GroupChat({
  group, user, members, promptAuth = () => {},
  // Pattern #2 freemium tease (chat gate). When `locked` is true, the
  // input is disabled with an "Unlock to chat" placeholder, a sticky
  // unlock bar (mirroring GolfDivisionsMode's) renders at the bottom,
  // and all three write paths (handleSend → sendGroupMessage;
  // toggleReaction's add branch → addReaction; handleDelete →
  // softDeleteMessage) intercept to onUnlockRequest() instead of
  // hitting the server. 041's RESTRICTIVE policies on group_messages
  // + message_reactions would 42501 the writes anyway; this prevents
  // the raw DB error from surfacing. 041 also blocks SELECTs so chat
  // history is empty on locked pools — the input + unlock bar are the
  // entire UX. When `locked` is false (default), behavior is byte-
  // identical to pre-Pattern-#2. removeReaction is NOT gated — DELETE
  // isn't in 041's RESTRICTIVE list and the existing permissive policy
  // on `auth.uid() = user_id` keeps it working.
  locked = false,
  onUnlockRequest,
  unlocking = false,
  unlockPriceCents = null,
  // Moderation (migration 053). Set of user_ids the current viewer has
  // blocked; their messages + reactions are filtered from the render.
  // onUserBlocked fires after a successful block so the parent can
  // update the shared blockedUserIds Set without a full refetch.
  blockedUserIds = new Set(),
  onUserBlocked = () => {},
}) {
  const [messages,    setMessages]    = useState([])
  const [inputValue,  setInputValue]  = useState('')
  const [sending,     setSending]     = useState(false)
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState(null)
  const [confirmDel,  setConfirmDel]  = useState(null)   // messageId pending delete-confirm
  // Moderation UI state — action sheet + report dialog + confirm-block dialog
  const [modMenuForMessageId, setModMenuForMessageId] = useState(null)
  const [reportForMessage,    setReportForMessage]    = useState(null)  // full msg object
  const [reportReason,        setReportReason]        = useState('other')
  const [reportSubmitting,    setReportSubmitting]    = useState(false)
  const [confirmBlockUser,    setConfirmBlockUser]    = useState(null)  // { user_id, username }
  const [blockSubmitting,     setBlockSubmitting]     = useState(false)
  // FIX 1 — chat-scoped lazy cache for senders missing from the `members`
  // prop. Keyed by user_id → {username, avatar_url}. See effect below for
  // the fetch trigger + caching strategy.
  const [profileCache, setProfileCache] = useState({})
  // A2 — reactions state + UI controls.
  // reactions: flat array, one row per (message_id, user_id, emoji).
  //   Grouped per-message + per-emoji at render time (cheap; ~50 messages
  //   × few reactions each is well under any perf threshold).
  // pickerOpenForMessageId: only one picker open at a time. null = closed.
  // fullPickerOpen: whether the lazy emoji-mart picker is expanded (vs
  //   just the 8-emoji quick row). Reset to false whenever pickerOpen
  //   changes message.
  // recentlyAdded: Set of `${messageId}:${emoji}` keys that should play the
  //   scale-pop animation. Auto-cleared via setTimeout after REACTION_POP_MS.
  const [reactions,              setReactions]              = useState([])
  const [pickerOpenForMessageId, setPickerOpenForMessageId] = useState(null)
  const [fullPickerOpen,         setFullPickerOpen]         = useState(false)
  const [recentlyAdded,          setRecentlyAdded]          = useState(() => new Set())

  // A3 — @mentions autocomplete state machine. Self-contained; triggered
  // by typing '@' followed by [A-Za-z0-9_.] runs in the textarea. Picker
  // renders inline above the input. Keyboard nav: ArrowUp/Down navigate
  // matches, Enter/Tab select (preempting the send), Escape closes.
  // Plain-text storage — the literal "@username" lands in body. Render-
  // side parser at the bottom of this file converts to chips at display
  // time. See .claude/chat-a3-snapshot.md for the full design rationale.
  const [mentionPickerOpen,  setMentionPickerOpen]  = useState(false)
  const [mentionQuery,       setMentionQuery]       = useState('')
  const [mentionAtIdx,       setMentionAtIdx]       = useState(-1)   // index of the @ that opened the picker
  const [mentionSelectedIdx, setMentionSelectedIdx] = useState(0)

  const channelRef          = useRef(null)
  const reactionsChannelRef = useRef(null)
  const scrollRef           = useRef(null)
  const textareaRef         = useRef(null)
  const isCreator     = group?.created_by === user?.id
  const isArchived    = group?.status === 'archived'
  // Track member name lookup; falls back to a truncated UUID if a sender
  // isn't in the loaded members list (e.g. legacy ex-member message).
  const memberById    = new Map((members || []).map(m => [m.user_id, m]))

  // ── Initial load + realtime subscribe ─────────────────────────────────
  useEffect(() => {
    if (!group?.id || !user?.id) return
    let cancelled = false
    setLoading(true)
    // A2 — combined load: messages + their reactions in one round trip.
    // Initial paint has reactions ready (no flicker-in pattern).
    loadGroupMessagesWithReactions(group.id).then(({ messages: msgs, reactions: rxns }) => {
      if (cancelled) return
      setMessages(msgs)
      setReactions(rxns)
      setLoading(false)
      // Initial scroll-to-bottom after the DOM paints the message list.
      requestAnimationFrame(() => scrollToBottom('auto'))
    })

    channelRef.current = subscribeToGroupMessages(group.id, (kind, msg) => {
      if (!msg) return
      setMessages(prev => {
        if (kind === 'insert') {
          // Dedup: own-user inserts arrived first via optimistic UI; the
          // realtime echo lands later with the same id (or, for races
          // where the realtime event beats the supabase-js INSERT
          // response, we replace the optimistic temp-id row). Index by
          // id, then by sender+body+created_at-proximity for temp rows.
          if (prev.some(m => m.id === msg.id)) return prev
          // Replace any optimistic temp row from the same user with the
          // same body that landed within the last 5s.
          const tempIdx = prev.findIndex(m =>
            m._optimistic
            && m.user_id === msg.user_id
            && m.body === msg.body
            && Math.abs(new Date(m.created_at).getTime() - new Date(msg.created_at).getTime()) < 5000
          )
          if (tempIdx >= 0) {
            const next = prev.slice()
            next[tempIdx] = msg
            return next
          }
          return [...prev, msg]
        }
        if (kind === 'update') {
          const idx = prev.findIndex(m => m.id === msg.id)
          if (idx < 0) return prev
          const next = prev.slice()
          next[idx] = msg
          return next
        }
        return prev
      })
    })

    // A2 — separate realtime channel for reactions. Filtered by the
    // denormalized group_id (trigger-populated, see migration 024 §1).
    // INSERT echo handles both own optimistic confirmations (dedup by id)
    // and other-users' new reactions; DELETE handles toggle-off + remote
    // deletes. Add scale-pop animation on INSERT (own or remote).
    reactionsChannelRef.current = subscribeToGroupReactions(group.id, (kind, row) => {
      if (!row) return
      if (kind === 'insert') {
        setReactions(prev => {
          if (prev.some(r => r.id === row.id)) return prev
          return [...prev, row]
        })
        triggerPop(row.message_id, row.emoji)
      } else if (kind === 'delete') {
        setReactions(prev => prev.filter(r => r.id !== row.id))
      }
    })

    return () => {
      cancelled = true
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        channelRef.current = null
      }
      if (reactionsChannelRef.current) {
        supabase.removeChannel(reactionsChannelRef.current)
        reactionsChannelRef.current = null
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id, user?.id])

  // ── A2: scale-pop animation tracker ───────────────────────────────────
  // Add `${messageId}:${emoji}` to recentlyAdded for REACTION_POP_MS, then
  // remove it. Render reads the Set to decide whether to apply the
  // transform: scale(1.2) inline style on the chip.
  function triggerPop(messageId, emoji) {
    const key = `${messageId}:${emoji}`
    setRecentlyAdded(prev => {
      if (prev.has(key)) return prev
      const next = new Set(prev)
      next.add(key)
      return next
    })
    setTimeout(() => {
      setRecentlyAdded(prev => {
        if (!prev.has(key)) return prev
        const next = new Set(prev)
        next.delete(key)
        return next
      })
    }, REACTION_POP_MS)
  }

  // ── Auto-scroll on new messages (gated by "was near bottom?") ─────────
  // Capture scroll position continuously via onScroll into a ref. Reading
  // scrollHeight inside the new-message effect is too late — by then the
  // appended row has already inflated scrollHeight and "distance from
  // bottom" looks like newMessageHeight (potentially > threshold for
  // long messages). The ref reflects the user's position BEFORE the new
  // row landed, which is what the gate should check.
  // Default to true so the first arrival (no prior scroll events) snaps.
  const prevCountRef     = useRef(0)
  const wasNearBottomRef = useRef(true)

  function handleScroll(e) {
    const el = e.currentTarget
    wasNearBottomRef.current =
      (el.scrollHeight - el.scrollTop - el.clientHeight) < SCROLL_BOTTOM_THRESHOLD
  }

  useEffect(() => {
    if (messages.length > prevCountRef.current && wasNearBottomRef.current) {
      scrollToBottom('smooth')
    }
    prevCountRef.current = messages.length
  }, [messages])

  function scrollToBottom(behavior = 'smooth') {
    const el = scrollRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior })
  }

  // ── Lazy profile fetch for senders missing from `members` ─────────────
  // The `members` prop is a one-shot fetch in GroupDetail and doesn't
  // refresh on membership changes. When a realtime message arrives whose
  // author joined the pool AFTER members was loaded, memberById.get()
  // misses and the sender shows as an 8-char UUID prefix. A1 mitigation:
  // batch-fetch missing profiles into a chat-scoped cache when messages
  // change. A3 (mentions) will replace this with a proper realtime
  // subscription to group_members in GroupDetail itself.
  // Cheap: single query batching all unknowns; dedups via the cache;
  // fires only when there are user_ids in messages we haven't resolved.
  useEffect(() => {
    if (!messages.length) return
    const uniqueUserIds = Array.from(new Set(messages.map(m => m.user_id).filter(Boolean)))
    const missing = uniqueUserIds.filter(uid => !memberById.has(uid) && !profileCache[uid])
    if (missing.length === 0) return
    let cancelled = false
    ;(async () => {
      const { data, error: profErr } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .in('id', missing)
      if (cancelled || profErr || !data?.length) return
      setProfileCache(prev => ({
        ...prev,
        ...Object.fromEntries(data.map(p => [p.id, { username: p.username, avatar_url: p.avatar_url }])),
      }))
    })()
    return () => { cancelled = true }
  // memberById is rebuilt each render but its keys are stable per `members`,
  // and profileCache is the dedupe ratchet — listing them as deps would
  // cause one redundant fire after a successful fetch (cache populated →
  // missing recomputed empty → no-op) but is otherwise correct. Omitting
  // them per the existing project pattern (see PickemMode useEffects).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages])

  // ── Send (with optimistic insert) ─────────────────────────────────────
  async function handleSend() {
    // Anonymous-browsing gate — sendGroupMessage needs user.id.
    if (!user?.id) return promptAuth()
    const trimmed = inputValue.trim()
    if (!trimmed || trimmed.length > MAX_LEN || sending || isArchived) return
    // Pattern #2 intercept — locked pool: route to unlock checkout
    // instead of writing. 041's group_messages_require_unlocked_insert
    // would 42501 the INSERT anyway.
    if (locked) {
      onUnlockRequest?.()
      return
    }
    // Profanity gate (Apple 1.2 content filter). Rejects on client only —
    // reported-and-blocked handles anything that slips through.
    if (containsProfanity(trimmed)) {
      setError('Please rephrase — that word isn\'t allowed.')
      return
    }
    setSending(true)
    setError(null)
    // Optimistic row — _optimistic marker for the realtime echo dedup logic.
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    const optimistic = {
      id:         tempId,
      group_id:   group.id,
      user_id:    user.id,
      body:       trimmed,
      created_at: new Date().toISOString(),
      deleted_at: null,
      _optimistic: true,
    }
    setMessages(prev => [...prev, optimistic])
    setInputValue('')
    requestAnimationFrame(() => scrollToBottom('smooth'))

    const { data, error: sendErr } = await sendGroupMessage(group.id, user.id, trimmed)
    if (sendErr) {
      log('[chat] send failed:', sendErr?.message)
      // Roll back the optimistic row + restore the input.
      setMessages(prev => prev.filter(m => m.id !== tempId))
      setInputValue(trimmed)
      setError(sendErr.message || 'Failed to send')
    } else if (data) {
      // Replace the optimistic row with the canonical one. The realtime
      // echo may also fire for this message; the dedup logic above handles
      // both arrival orders.
      setMessages(prev => {
        const idx = prev.findIndex(m => m.id === tempId)
        if (idx < 0) {
          if (prev.some(m => m.id === data.id)) return prev   // realtime already landed
          return [...prev, data]
        }
        const next = prev.slice()
        next[idx] = data
        return next
      })
    }
    setSending(false)
  }

  async function handleDelete(messageId) {
    // Anonymous-browsing gate — delete writes need user.id.
    if (!user?.id) return promptAuth()
    // Pattern #2 intercept — defense-in-depth. UI-unreachable on a
    // locked pool today (chat history is empty so no message exists to
    // delete), but 041's group_messages_require_unlocked_update would
    // 42501 the UPDATE if anything ever routed here.
    if (locked) {
      onUnlockRequest?.()
      return
    }
    setConfirmDel(null)
    const { error: delErr } = await softDeleteMessage(messageId)
    if (delErr) {
      log('[chat] delete failed:', delErr?.message)
      setError(delErr.message || 'Failed to delete')
    }
    // Successful soft-delete arrives via the UPDATE realtime echo; no local
    // state mutation needed here.
  }

  // ── Moderation actions (migration 053) ────────────────────────────────
  async function submitReport() {
    if (!user?.id || !reportForMessage) return
    setReportSubmitting(true)
    const { error: repErr } = await fileContentReport({
      reporterUserId:  user.id,
      reportedUserId:  reportForMessage.user_id,
      groupId:         group.id,
      messageId:       reportForMessage.id,
      contentSnapshot: reportForMessage.body,
      reason:          reportReason,
    })
    setReportSubmitting(false)
    if (repErr) {
      setError(repErr.message || 'Could not submit report')
      return
    }
    setReportForMessage(null)
    setReportReason('other')
    setModMenuForMessageId(null)
    // Non-blocking confirmation via the existing error banner slot,
    // styled as informational (setError with a "success" prefix would be
    // weird — a brief toast is nicer but there's no toast in scope here).
    // Simplest: transient success message reusing setError.
    setError('Thanks — we\'ll review within 24 hours.')
    setTimeout(() => setError(prev => prev === 'Thanks — we\'ll review within 24 hours.' ? null : prev), 4000)
  }

  async function submitBlock() {
    if (!user?.id || !confirmBlockUser?.user_id) return
    setBlockSubmitting(true)
    const { error: blkErr } = await blockUser(user.id, confirmBlockUser.user_id)
    setBlockSubmitting(false)
    if (blkErr) {
      setError(blkErr.message || 'Could not block user')
      return
    }
    // Notify parent so its shared blockedUserIds Set updates too — this
    // component filters locally by prop, so we don't need to do anything
    // else here; the parent's update will re-render with new prop.
    onUserBlocked(confirmBlockUser.user_id)
    setConfirmBlockUser(null)
    setModMenuForMessageId(null)
  }

  // ── A2: reaction toggle with optimistic UI ────────────────────────────
  // Local-state inference (per snapshot §4): if my reaction row exists in
  // current state → DELETE; else → INSERT. Single round trip.
  // Stale-state edge cases (e.g. user reacted from another device since
  // last realtime tick) fail benignly: INSERT hits UNIQUE → optimistic
  // row rolled back; DELETE no-ops if the row was already gone.
  async function toggleReaction(messageId, emoji) {
    // Anonymous-browsing gate — addReaction/removeReaction need user.id.
    if (!user?.id) return promptAuth()
    // Always close the picker after a quick-row tap; full-picker close
    // handled in the picker's onEmojiSelect.
    setPickerOpenForMessageId(null)
    setFullPickerOpen(false)

    const mine = reactions.find(r =>
      r.message_id === messageId && r.user_id === user.id && r.emoji === emoji
    )
    if (mine) {
      // Optimistic remove.
      setReactions(prev => prev.filter(r => r.id !== mine.id))
      const { error: rmErr } = await removeReaction(messageId, user.id, emoji)
      if (rmErr) {
        log('[chat] removeReaction failed:', rmErr?.message)
        // Restore — best-effort; realtime echo will normalize anyway.
        setReactions(prev => prev.some(r => r.id === mine.id) ? prev : [...prev, mine])
        setError(rmErr.message || 'Failed to remove reaction')
      }
    } else {
      // Pattern #2 intercept — locked pool: route to unlock checkout
      // instead of adding. 041's message_reactions_require_unlocked_insert
      // would 42501 the INSERT anyway. removeReaction (the `if (mine)`
      // branch above) is NOT gated — DELETE isn't in 041's RESTRICTIVE
      // list and the existing permissive policy keeps it working.
      if (locked) {
        onUnlockRequest?.()
        return
      }
      // Optimistic add. Temp id so realtime-echo dedup can replace it.
      const tempId = `temp-rx-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
      const optimistic = {
        id:         tempId,
        message_id: messageId,
        user_id:    user.id,
        emoji,
        created_at: new Date().toISOString(),
        _optimistic: true,
      }
      setReactions(prev => [...prev, optimistic])
      triggerPop(messageId, emoji)
      const { data, error: addErr } = await addReaction(messageId, user.id, emoji)
      if (addErr) {
        log('[chat] addReaction failed:', addErr?.message)
        // Roll back the optimistic chip.
        setReactions(prev => prev.filter(r => r.id !== tempId))
        // UNIQUE-violation (code 23505) is the expected stale-state
        // outcome — silent rollback, no UI noise. Other errors surface.
        if (addErr.code !== '23505') {
          setError(addErr.message || 'Failed to add reaction')
        }
      } else if (data) {
        // Replace the optimistic row with the canonical one. Realtime
        // INSERT echo may also fire; the channel handler dedups by id.
        setReactions(prev => {
          const idx = prev.findIndex(r => r.id === tempId)
          if (idx < 0) {
            if (prev.some(r => r.id === data.id)) return prev   // realtime won the race
            return [...prev, data]
          }
          const next = prev.slice()
          next[idx] = data
          return next
        })
      }
    }
  }

  // Toggle picker open for a message, or close if already open.
  function togglePicker(messageId) {
    setFullPickerOpen(false)
    setPickerOpenForMessageId(prev => prev === messageId ? null : messageId)
  }

  // A3 — derive autocomplete matches from current query + members.
  // Case-insensitive prefix match preferred; fall back to includes() if
  // prefix yields nothing. Exclude self (can't @yourself). Cap at 5.
  const mentionMatches = (() => {
    if (!mentionPickerOpen) return []
    const q = mentionQuery.toLowerCase()
    const pool = (members || []).filter(m =>
      m.user_id !== user?.id
      && typeof m.username === 'string'
      // Only mentionable usernames — same conservative charset as the
      // render-side parser regex. Skip usernames that contain chars the
      // parser couldn't match anyway (spaces, unicode, punctuation
      // outside _.) so the dropdown doesn't promise something we can't
      // honor.
      && /^[A-Za-z0-9_.]+$/.test(m.username)
    )
    if (!q) return pool.slice(0, 5)
    const prefix = pool.filter(m => m.username.toLowerCase().startsWith(q))
    if (prefix.length > 0) return prefix.slice(0, 5)
    return pool.filter(m => m.username.toLowerCase().includes(q)).slice(0, 5)
  })()

  // A3 — on every input change, recompute mention context. Look BACKWARD
  // from the cursor for the most recent '@' that isn't preceded by a
  // word char (mirrors the render-parser lookbehind; otherwise typing
  // "user@email" inside an email opens the picker spuriously). The
  // chars between '@' and cursor must all be [A-Za-z0-9_.] — any other
  // char closes the picker.
  function detectMentionContext(value, caret) {
    let i = caret - 1
    while (i >= 0) {
      const ch = value[i]
      if (ch === '@') {
        // Verify the @ isn't preceded by a word char (email-in-text
        // case). Mirrors the render-parser lookbehind.
        const prev = i > 0 ? value[i - 1] : ''
        if (prev && /[A-Za-z0-9_.]/.test(prev)) return null
        const query = value.slice(i + 1, caret)
        if (!/^[A-Za-z0-9_.]*$/.test(query)) return null
        return { atIdx: i, query }
      }
      if (!/[A-Za-z0-9_.]/.test(ch)) return null   // hit a boundary; no active @
      i--
    }
    return null
  }

  function handleInputChange(e) {
    const value = e.target.value
    setInputValue(value)
    const caret = e.target.selectionStart
    const ctx = detectMentionContext(value, caret)
    if (ctx) {
      setMentionPickerOpen(true)
      setMentionQuery(ctx.query)
      setMentionAtIdx(ctx.atIdx)
      setMentionSelectedIdx(0)
    } else if (mentionPickerOpen) {
      setMentionPickerOpen(false)
      setMentionQuery('')
      setMentionAtIdx(-1)
    }
  }

  // A3 — insert the selected member's @username at the @ position,
  // replacing whatever query text the user had typed. Trailing space so
  // the next keystroke isn't part of the username regex. Cursor lands
  // just after the inserted text + space.
  function insertMention(member) {
    if (!member || mentionAtIdx < 0 || !textareaRef.current) return
    // Defensive — re-validate that the captured mentionAtIdx still matches
    // the current input state. If state diverged (e.g. a future cursor-move
    // handler reopened the picker without updating mentionAtIdx), close
    // without inserting rather than mutate corrupted state.
    const ctx = detectMentionContext(inputValue, textareaRef.current.selectionStart)
    if (!ctx || ctx.atIdx !== mentionAtIdx) {
      setMentionPickerOpen(false)
      setMentionQuery('')
      setMentionAtIdx(-1)
      return
    }
    const before    = inputValue.slice(0, mentionAtIdx)
    const caret     = textareaRef.current.selectionStart
    const after     = inputValue.slice(caret)
    const insertion = `@${member.username} `
    const newValue  = before + insertion + after
    const newCaret  = mentionAtIdx + insertion.length
    setInputValue(newValue)
    setMentionPickerOpen(false)
    setMentionQuery('')
    setMentionAtIdx(-1)
    // Restore focus + cursor after React commits the new value.
    requestAnimationFrame(() => {
      const el = textareaRef.current
      if (!el) return
      el.focus()
      el.setSelectionRange(newCaret, newCaret)
    })
  }

  function handleKeyDown(e) {
    // A3 — picker-open keys preempt the existing Enter-to-send.
    if (mentionPickerOpen && mentionMatches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionSelectedIdx(i => Math.min(i + 1, mentionMatches.length - 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionSelectedIdx(i => Math.max(i - 1, 0))
        return
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault()
        insertMention(mentionMatches[mentionSelectedIdx])
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setMentionPickerOpen(false)
        setMentionQuery('')
        setMentionAtIdx(-1)
        return
      }
    }
    // Picker closed (or no matches): existing Enter-to-send behavior.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // ── Render ────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ textAlign:'center', padding:'40px', color:'var(--color-text-dim)' }}>
      Loading chat…
    </div>
  )

  return (
    <div style={{
      display:'flex', flexDirection:'column',
      // dvh adapts to mobile keyboard; fallback for older Safari (≤15.4)
      // shrinks slightly but stays usable. min(...) caps to viewport.
      height:'min(560px, 70dvh)',
    }}>
      {/* Message list */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        style={{
          flex:1, overflowY:'auto', padding:'4px 2px',
          display:'flex', flexDirection:'column', gap:'8px',
          background:'var(--color-card-2)', borderRadius:'8px',
          border:'1px solid var(--color-border)',
        }}>
        {messages.length === 0 ? (
          <div style={{ textAlign:'center', padding:'40px 16px', color:'var(--color-text-dim)', fontSize:'13px' }}>
            No messages yet. Say something!
          </div>
        ) : (() => {
          // A3 — hoist the valid-usernames Set out of the per-message loop
          // (free perf win: built once per render instead of 50× inside
          // the map). Lowercased for case-insensitive matching in the
          // render-side parser.
          const validUsernamesSet = new Set(
            (members || []).map(m => m.username?.toLowerCase()).filter(Boolean)
          )
          // Filter blocked users' messages + reactions from the render.
          // Migration 053 client-side hide — the messages/reactions still
          // exist in DB (blocked user doesn't know), just not shown to viewer.
          const visibleMessages = blockedUserIds.size > 0
            ? messages.filter(m => !blockedUserIds.has(m.user_id))
            : messages
          return visibleMessages.map(msg => {
          const isOwn       = msg.user_id === user.id
          const isDeleted   = !!msg.deleted_at
          // FIX 1 — fall through memberById → profileCache → UUID prefix.
          const sender      = memberById.get(msg.user_id) || profileCache[msg.user_id]
          const senderName  = sender?.username || msg.user_id?.slice(0, 8) || 'unknown'
          const canDelete   = !isDeleted && (isOwn || isCreator)
          const canReact    = !isDeleted
          const pickerOpen  = pickerOpenForMessageId === msg.id && !isDeleted
          // A2 — group this message's reactions by emoji; sort desc by count.
          // Skip entirely on soft-deleted messages (chips hidden, rows stay).
          const msgReactions = isDeleted
            ? []
            : reactions.filter(r => r.message_id === msg.id)
          const groupedReactions = isDeleted
            ? []
            : Array.from(
                msgReactions.reduce((map, r) => {
                  if (!map.has(r.emoji)) map.set(r.emoji, { emoji: r.emoji, users: [] })
                  map.get(r.emoji).users.push(r.user_id)
                  return map
                }, new Map()).values()
              ).sort((a, b) => b.users.length - a.users.length)
          return (
            <div key={msg.id} style={{
              display:'flex', flexDirection:'column',
              alignItems: isOwn ? 'flex-end' : 'flex-start',
              padding:'2px 8px',
            }}>
              <div style={{
                fontSize:'10px', color:'var(--color-text-muted)', marginBottom:'2px',
                fontFamily:'Arial,sans-serif',
              }}>
                {isOwn ? 'You' : senderName} · {timeAgo(msg.created_at) || 'just now'}
              </div>
              <div style={{
                maxWidth:'80%',
                background: isDeleted
                  ? 'transparent'
                  : (isOwn ? 'var(--color-primary-dim)' : 'var(--color-card)'),
                border: `1px solid ${isDeleted ? 'var(--color-border)' : (isOwn ? 'var(--color-primary-dim2)' : 'var(--color-border)')}`,
                borderRadius:'12px', padding:'8px 12px',
                color: isDeleted ? 'var(--color-text-muted)' : 'var(--color-text)',
                fontSize:'14px', lineHeight:1.4,
                fontStyle: isDeleted ? 'italic' : 'normal',
                wordBreak:'break-word', whiteSpace:'pre-wrap',
                opacity: msg._optimistic ? 0.65 : 1,
              }}>
                {isDeleted
                  ? '[deleted]'
                  : renderBodyWithMentions(msg.body, validUsernamesSet, user?.username)}
              </div>

              {/* A2 — reaction chips. Grouped by emoji, sorted desc by count.
                  Each chip is tappable: toggles current user's inclusion in
                  that emoji. Active styling = current user is among reactors. */}
              {groupedReactions.length > 0 && (
                <div style={{
                  display:'flex', flexWrap:'wrap', gap:'4px', marginTop:'4px',
                  justifyContent: isOwn ? 'flex-end' : 'flex-start',
                }}>
                  {groupedReactions.map(({ emoji, users }) => {
                    const mine    = users.includes(user.id)
                    const popping = recentlyAdded.has(`${msg.id}:${emoji}`)
                    return (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => toggleReaction(msg.id, emoji)}
                        style={{
                          background: mine ? 'var(--color-primary-dim)' : 'var(--color-card-2)',
                          border:     `1px solid ${mine ? 'var(--color-primary-dim2)' : 'var(--color-border)'}`,
                          color:      mine ? 'var(--color-primary)' : 'var(--color-text)',
                          borderRadius:'10px', padding:'2px 6px',
                          fontSize:'12px', fontFamily:'inherit', cursor:'pointer',
                          display:'flex', alignItems:'center', gap:'3px',
                          // Scale-pop on add — transform + transition does the
                          // animation without keyframe CSS. Returns to 1 when
                          // recentlyAdded clears (auto after REACTION_POP_MS).
                          transform:  popping ? 'scale(1.2)' : 'scale(1)',
                          transition: `transform ${REACTION_POP_MS}ms ease-out`,
                        }}>
                        <span>{emoji}</span>
                        <span style={{ fontFamily:'Arial,sans-serif', fontWeight:'bold' }}>{users.length}</span>
                      </button>
                    )
                  })}
                </div>
              )}

              {/* Action row — react + delete affordances. Sit together in one
                  small muted row below the bubble (and below the chips, if any).
                  Confirm-delete state takes over the row when active. */}
              {(canReact || canDelete) && (
                confirmDel === msg.id ? (
                  <div style={{ display:'flex', gap:'6px', marginTop:'4px' }}>
                    <button onClick={() => handleDelete(msg.id)} style={{
                      background:'transparent', border:'1px solid var(--color-error, #b94)',
                      color:'var(--color-error, #b94)', borderRadius:'6px',
                      padding:'2px 8px', fontSize:'11px', cursor:'pointer',
                      fontFamily:'Arial,sans-serif',
                    }}>
                      {/* FIX 2 — moderation copy makes "visible to all" explicit. */}
                      {isOwn ? 'Confirm delete' : 'Confirm remove (visible to all)'}
                    </button>
                    <button onClick={() => setConfirmDel(null)} style={{
                      background:'transparent', border:'1px solid var(--color-border)',
                      color:'var(--color-text-dim)', borderRadius:'6px',
                      padding:'2px 8px', fontSize:'11px', cursor:'pointer',
                      fontFamily:'Arial,sans-serif',
                    }}>Cancel</button>
                  </div>
                ) : (
                  <div style={{ display:'flex', gap:'10px', marginTop:'2px' }}>
                    {canReact && (
                      <button onClick={() => togglePicker(msg.id)} style={{
                        background:'transparent', border:'none',
                        color: pickerOpen ? 'var(--color-primary)' : 'var(--color-text-muted)',
                        cursor:'pointer', padding:'2px 4px', fontSize:'10px',
                        fontFamily:'Arial,sans-serif',
                      }}>
                        {pickerOpen ? 'close' : 'react'}
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => setConfirmDel(msg.id)} style={{
                        background:'transparent', border:'none',
                        color:'var(--color-text-muted)', cursor:'pointer',
                        padding:'2px 4px', fontSize:'10px',
                        fontFamily:'Arial,sans-serif',
                      }}>
                        delete
                      </button>
                    )}
                    {/* Moderation menu — report + block. Only for OTHERS'
                        messages (own messages don't need self-report/block).
                        Deleted messages skip too (nothing to moderate).  */}
                    {!isOwn && !isDeleted && user?.id && (
                      <button onClick={() => setModMenuForMessageId(
                        modMenuForMessageId === msg.id ? null : msg.id
                      )} aria-label="More actions" style={{
                        background:'transparent', border:'none',
                        color:'var(--color-text-muted)', cursor:'pointer',
                        padding:'2px 4px', fontSize:'10px',
                        fontFamily:'Arial,sans-serif',
                      }}>
                        ⋯
                      </button>
                    )}
                  </div>
                )
              )}

              {/* Moderation menu sheet — expands inline when ⋯ is tapped.
                  Report opens the reason picker; Block opens confirm. */}
              {modMenuForMessageId === msg.id && !isOwn && !isDeleted && (
                <div style={{
                  marginTop:'4px', display:'flex', flexDirection:'column', gap:'4px',
                  background:'var(--color-card-2)',
                  border:'1px solid var(--color-border)',
                  borderRadius:'8px', padding:'6px',
                  minWidth:'160px',
                }}>
                  <button onClick={() => { setReportForMessage(msg); setModMenuForMessageId(null) }} style={{
                    background:'transparent', border:'none',
                    color:'var(--color-text)', cursor:'pointer',
                    padding:'6px 8px', fontSize:'12px',
                    textAlign:'left', fontFamily:'Arial,sans-serif',
                  }}>
                    Report message
                  </button>
                  <button onClick={() => { setConfirmBlockUser({ user_id: msg.user_id, username: senderName }); setModMenuForMessageId(null) }} style={{
                    background:'transparent', border:'none',
                    color:'var(--color-text)', cursor:'pointer',
                    padding:'6px 8px', fontSize:'12px',
                    textAlign:'left', fontFamily:'Arial,sans-serif',
                  }}>
                    Block user
                  </button>
                  <button onClick={() => setModMenuForMessageId(null)} style={{
                    background:'transparent', border:'none',
                    color:'var(--color-text-dim)', cursor:'pointer',
                    padding:'6px 8px', fontSize:'11px',
                    textAlign:'left', fontFamily:'Arial,sans-serif',
                  }}>
                    Cancel
                  </button>
                </div>
              )}

              {/* A2 — inline picker. Quick row + "+" expanding to lazy
                  emoji-mart. Only one message has its picker open at a
                  time (state is a single messageId, not a Set). */}
              {pickerOpen && (
                <div style={{
                  marginTop:'4px',
                  background:'var(--color-card-2)',
                  border:'1px solid var(--color-border)',
                  borderRadius:'10px', padding:'6px',
                  // Cap so the inline picker doesn't blow up the chat
                  // scroll viewport. Full picker scrolls internally.
                  maxWidth:'320px',
                }}>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'4px' }}>
                    {QUICK_REACTIONS.map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => toggleReaction(msg.id, emoji)}
                        style={{
                          background:'var(--color-card)',
                          border:'1px solid var(--color-border)',
                          borderRadius:'8px', padding:'4px 8px',
                          fontSize:'18px', cursor:'pointer',
                          lineHeight:1,
                        }}>
                        {emoji}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => setFullPickerOpen(o => !o)}
                      style={{
                        background: fullPickerOpen ? 'var(--color-primary-dim)' : 'var(--color-card)',
                        border:`1px solid ${fullPickerOpen ? 'var(--color-primary-dim2)' : 'var(--color-border)'}`,
                        color: fullPickerOpen ? 'var(--color-primary)' : 'var(--color-text-dim)',
                        borderRadius:'8px', padding:'4px 10px',
                        fontSize:'14px', cursor:'pointer', fontFamily:'Arial,sans-serif',
                        lineHeight:1,
                      }}>
                      {fullPickerOpen ? '−' : '+'}
                    </button>
                  </div>
                  {fullPickerOpen && (
                    <div style={{ marginTop:'6px' }}>
                      <Suspense fallback={
                        <div style={{
                          textAlign:'center', padding:'12px',
                          color:'var(--color-text-dim)', fontSize:'12px',
                        }}>Loading picker…</div>
                      }>
                        <FullEmojiPicker
                          data={async () => fetch(EMOJI_DATA_URL).then(r => r.json())}
                          onEmojiSelect={(e) => toggleReaction(msg.id, e.native)}
                          theme="dark"
                          previewPosition="none"
                          skinTonePosition="none"
                        />
                      </Suspense>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })
        })()}
      </div>

      {/* Input row OR archived notice */}
      {isArchived ? (
        <div className="alert alert-info" style={{
          marginTop:'10px', fontSize:'12px', textAlign:'center',
        }}>
          This group is archived — chat is read-only.
        </div>
      ) : (
        <div style={{ marginTop:'10px' }}>
          {error && (
            <div className="alert alert-error" style={{ marginBottom:'8px', fontSize:'12px' }}>
              {error}
            </div>
          )}
          {/* A3 — position: relative so the mention picker can anchor
              above the input via bottom:100%. */}
          <div style={{ position:'relative' }}>
            {mentionPickerOpen && mentionMatches.length > 0 && (
              <div style={{
                position:'absolute', bottom:'100%', left:0, right:0,
                marginBottom:'4px',
                background:'var(--color-card)', border:'1px solid var(--color-border)',
                borderRadius:'8px', boxShadow:'0 -2px 8px rgba(0,0,0,0.3)',
                // 40dvh ceiling so the picker shrinks gracefully on very small viewports
                // (e.g. iPhone SE landscape with keyboard open). 180px on normal phones.
                maxHeight:'min(180px, 40dvh)', overflowY:'auto', zIndex:10,
              }}>
                {mentionMatches.map((m, i) => {
                  const active = i === mentionSelectedIdx
                  return (
                    <button
                      key={m.user_id}
                      type="button"
                      // onMouseDown (not onClick) — onClick would lose
                      // focus from the textarea before the insertion
                      // completes, breaking setSelectionRange's effect.
                      onMouseDown={(e) => { e.preventDefault(); insertMention(m) }}
                      onMouseEnter={() => setMentionSelectedIdx(i)}
                      style={{
                        display:'flex', alignItems:'center', gap:'8px',
                        width:'100%', padding:'6px 10px',
                        background: active ? 'var(--color-primary-dim)' : 'transparent',
                        color: active ? 'var(--color-primary)' : 'var(--color-text)',
                        border:'none', cursor:'pointer',
                        textAlign:'left', fontFamily:'inherit', fontSize:'14px',
                      }}>
                      {m.avatar_url ? (
                        <img src={m.avatar_url} alt="" style={{
                          width:20, height:20, borderRadius:'50%', objectFit:'cover',
                        }} />
                      ) : (
                        <span style={{
                          width:20, height:20, borderRadius:'50%',
                          background:'var(--color-card-2)', color:'var(--color-text-dim)',
                          fontSize:11, display:'inline-flex',
                          alignItems:'center', justifyContent:'center',
                        }}>{m.username?.[0]?.toUpperCase() || '?'}</span>
                      )}
                      <span>@{m.username}</span>
                    </button>
                  )
                })}
              </div>
            )}
            <div style={{ display:'flex', gap:'8px', alignItems:'flex-end' }}>
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={locked ? 'Unlock to chat' : 'Message the group… (Enter to send, Shift+Enter for newline)'}
                rows={2}
                maxLength={MAX_LEN}
                disabled={sending || locked}
                style={{
                  flex:1, resize:'none', fontFamily:'inherit', fontSize:'14px',
                  background:'var(--color-card)', color:'var(--color-text)',
                  border:'1px solid var(--color-border)', borderRadius:'8px',
                  padding:'8px 10px', minHeight:'40px',
                }}
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || inputValue.length > MAX_LEN || sending || locked}
                className="btn btn-gold"
                style={{ width:'auto', flexShrink:0, padding:'8px 14px' }}>
                {sending ? '…' : 'Send'}
              </button>
            </div>
          </div>
          <div style={{
            textAlign:'right', fontSize:'10px',
            color: inputValue.length > MAX_LEN ? 'var(--color-error, #b94)' : 'var(--color-text-muted)',
            fontFamily:'Arial,sans-serif', marginTop:'2px',
          }}>
            {inputValue.length}/{MAX_LEN}
          </div>
        </div>
      )}

      {/* Pattern #2 sticky unlock bar — mirrors GolfDivisionsMode's
          (commit 66f2a6b): fixed position, root-context z:90 (above
          .bottom-nav z:50, below modals z:100+), gold border, single
          line "🔒 Pool is locked / Unlock to chat" + Unlock button.
          Suppressed when archived (archive notice already explains the
          state; the unlock bar would be misleading on a permanently-
          archived pool). */}
      {locked && !isArchived && (
        <div
          style={{
            position: 'fixed',
            bottom: 80,
            left: 16,
            right: 16,
            maxWidth: 500,
            margin: '0 auto',
            background: 'var(--surface2)',
            border: '1px solid var(--gold)',
            borderRadius: 12,
            padding: '12px 14px',
            zIndex: 90,
            boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
              🔒 Pool is locked
            </div>
            <div style={{
              fontSize: 11, color: 'var(--text-dim)', marginTop: 2,
              fontFamily: 'Arial, sans-serif',
            }}>
              Unlock to chat
            </div>
          </div>
          <button
            type="button"
            onClick={onUnlockRequest}
            disabled={unlocking}
            className="btn btn-gold"
            style={{
              whiteSpace: 'nowrap',
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 700,
              opacity: unlocking ? 0.6 : 1,
              cursor: unlocking ? 'wait' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {unlocking
              ? 'Unlocking…'
              : (unlockPriceCents != null
                  ? `Unlock $${(unlockPriceCents / 100).toFixed(2)}`
                  : 'Unlock')}
          </button>
        </div>
      )}

      {/* ── Report message modal (migration 053) ────────────────────── */}
      {reportForMessage && (
        <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && setReportForMessage(null)}>
          <div className="modal">
            <div className="modal-title">Report message</div>
            <div style={{ marginBottom:'12px', padding:'8px', background:'var(--color-card-2)',
                          border:'1px solid var(--color-border)', borderRadius:'6px',
                          fontSize:'12px', color:'var(--color-text-dim)',
                          maxHeight:'80px', overflowY:'auto', whiteSpace:'pre-wrap',
                          wordBreak:'break-word' }}>
              {reportForMessage.body}
            </div>
            <div className="form-label">Why are you reporting this?</div>
            <select value={reportReason} onChange={e => setReportReason(e.target.value)}
                    style={{ marginBottom:'16px' }}>
              <option value="spam">Spam</option>
              <option value="harassment">Harassment / bullying</option>
              <option value="hate">Hate speech</option>
              <option value="sexual">Sexual content</option>
              <option value="other">Other</option>
            </select>
            <div style={{ display:'flex', gap:'8px' }}>
              <button className="btn btn-ghost" onClick={() => setReportForMessage(null)}
                      disabled={reportSubmitting} style={{ flex:1 }}>
                Cancel
              </button>
              <button className="btn btn-gold" onClick={submitReport}
                      disabled={reportSubmitting} style={{ flex:1 }}>
                {reportSubmitting ? 'Submitting…' : 'Submit report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm-block modal (migration 053) ─────────────────────── */}
      {confirmBlockUser && (
        <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && setConfirmBlockUser(null)}>
          <div className="modal">
            <div className="modal-title">Block {confirmBlockUser.username}?</div>
            <div style={{ marginBottom:'16px', color:'var(--color-text-dim)', fontSize:'13px', lineHeight:1.5 }}>
              You won't see their messages or reactions in any pool. You can
              unblock them later from your profile.
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              <button className="btn btn-ghost" onClick={() => setConfirmBlockUser(null)}
                      disabled={blockSubmitting} style={{ flex:1 }}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={submitBlock}
                      disabled={blockSubmitting} style={{ flex:1 }}>
                {blockSubmitting ? 'Blocking…' : 'Block user'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
